import { Outlet, useLocation } from "react-router";
import { BranchFrame } from "@/features/branches";
import { SalonBranding } from "@/features/salon";
import { push } from "@/lib/api";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { FloatingBook } from "./FloatingBook";
import { SalonHeader } from "./SalonHeader";
import { SiteFooter } from "./SiteFooter";

/**
 * A phone in a salon, not a desk in an office.
 *
 * No sidebar and no tab bar: a customer goes three places, and they sit in the switcher under the
 * salon's name, with the account on the avatar beside it (design note salon-booking-design-2.md S1).
 * The thumb zone belongs to the floating Book button, or to a basket bar once something is picked.
 * The staff app is the one with a sidebar, because a receptionist moves between ten screens a day.
 *
 * Inside the booking flow the footer and the floating button step aside: the flow has its own action
 * bar, and a customer halfway through a booking should finish it, not wander.
 *
 * Every screen sits inside the salon's branding, so its colour, fonts, name and theme colour are
 * right on whichever page a customer lands on first (TDD-SA FR-SA-122, FR-SA-140).
 */
export function AppLayout() {
  const { pathname } = useLocation();
  const inFlow = pathname.startsWith("/book");
  // On a phone the account is a page of its own with a Back bar (design note A1), not one of the three places.
  const wide = useMediaQuery("(min-width: 768px)");
  const accountOnPhone = pathname.startsWith("/account") && !wide;
  // The menu is where treatments are chosen; its own Add buttons and basket lead into booking, so the
  // floating Book button would only cover a card there (owner request, 2026-10-28).
  const onMenu = pathname.startsWith("/services");

  return (
    <SalonBranding>
      <div className="flex min-h-screen flex-col bg-base font-body text-content">
        {accountOnPhone ? null : <SalonHeader />}
        <main className="flex w-full flex-1 flex-col">
          {/* Keeps this phone's registration fresh and shows a push that arrives while the page is open. */}
          <push.PushBootstrap />
          {/* A branch's page, or the branch picker when the link names none at a salon with several (FR-SA-245). */}
          <BranchFrame>
            <Outlet />
          </BranchFrame>
        </main>
        {inFlow || accountOnPhone ? null : (
          <>
            <SiteFooter />
            {onMenu ? null : <FloatingBook />}
          </>
        )}
      </div>
    </SalonBranding>
  );
}
